import {
  Component,
  OnInit,
  Host,
  ViewChild,
  ElementRef,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  Inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from "@angular/core";
import { FormGroup, FormControl } from "@angular/forms";
import { Router, ActivatedRoute } from "@angular/router";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { catchError, debounceTime, map } from "rxjs/operators";
import {
  IRetailerOption,
  IOfferCard,
  IOfferCardFormData,
  IOfferCardDetail,
  IUser,
  IOfferRecipient,
  IAttachment,
  IUserAssistantDto,
  ICampaignTypeDto,
  IOfferCardCalendarCampaignDetail,
  IOfferAssistantDto
} from "src/app/models";
import {
  LxmMessage,
  DateHandler,
  LxmDialog,
  HttpBadRequestResponse,
  HttpUnauthorizedResponse
} from "src/app/_helpers";
import {
  OfferService,
  AuthenticationService,
  SignalRService,
  TitleService,
  FileService,
  ExportService
} from "src/app/_services";
import { OfferLayoutComponent } from "src/app/views/offers/offer/offer.layout";
import {
  OfferType,
  SupplierOfferStatus,
  UserAction,
  OfferKind,
  LxmAppModule,
  OfferChangeType,
  UniqueCodeTypeOption,
  OfferProductStatus,
  asArray
} from "src/app/enum";
import { AppState } from "src/app/state/app.state";
import { IClvDto } from "../product/product-info/product-info.model";
import { mergeMap } from "rxjs/operators";

import { RouteNavigator } from "src/app/_helpers/route.listener";
import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import { OfferExportDialogComponent } from "./dialogs/export/export-offer.dialog";
import { BehaviorSubject, EMPTY, Observable, Subscription, merge, of } from "rxjs";
import { MatSnackBarRef } from "@angular/material/snack-bar";
import { MessageComponent } from "src/app/components/_layout/messages/message/message.component";
import { RETAILER_ID } from "src/app/config";
import { TranslateService } from "@ngx-translate/core";
import { PipedriveService } from "src/app/_services/pipedrive.service";
import { FilesListComponent } from "src/app/components/_partials/files-list/files-list.component";
import { TranslatedValuePipe } from "src/app/pipes";
import { OfferExportType } from "src/app/enum/offer-export-type";
import { ORDERABLE_OFFER_TYPES } from "src/app/config";
import moment from "moment";
import { HttpEventType } from "@angular/common/http";
import { ImportsService } from "src/app/modules/imports/services/imports-service";
import { AddContactDialogComponent } from "src/app/modules/clients/cards";
import { appSettings } from "src/app/app.settings";
import { ClientsService } from "src/app/_services/clients.service";
import { CalendarGroupCampaignType } from "src/app/enum/calendar-group-campaign-type";
import { IUserLocked } from "src/app/components/_elements/lock/locked-info.component";
import { SupplierOfferProductsListComponent } from "src/app/components/_partials/offer-products/supplier-offer/supplier-offer-products.component";
import { stringIsNullOrEmpty } from "src/app/util/fn";
import { OfferAssistantNotificationType } from "src/app/enum/offer-assistant-notification-type";

@Component({
  selector: "offer-card",
  templateUrl: "./offer.card.html",
  styleUrls: ["./offer.card.scss"],
  host: {
    class: "flex flex-col gap-inherit"
  },
  providers: [PipedriveService, TranslatedValuePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OfferCardComponent implements OnInit, OnDestroy {
  private _msgRef: MatSnackBarRef<MessageComponent>;

  @ViewChild("download") private _download: ElementRef;
  @ViewChild(SupplierOfferProductsListComponent)
  productsList: SupplierOfferProductsListComponent;

  @Input() public isEditable: boolean;

  @Output() editing = new EventEmitter();
  @Output() onFormChange = new EventEmitter();

  public translationsPath = "cards.offer.base_info";
  public OfferStatus = SupplierOfferStatus;
  public LxmAppModule = LxmAppModule;
  public UserAction = UserAction;
  public OfferType = OfferType;
  public procurements;
  public calendarCampaigns;

  public offerId: string;

  public includeProductsPdfDisabled = false;
  public includeConformityDeclarationsPdfDisabled = false;

  public isLockedSubject$ = new BehaviorSubject<IUserLocked>(null);

  public get isLocked(): boolean {
    return this.isLockedSubject$.value ? true : false;
  }

  public get isLockedByCurrentUser(): boolean {
    return this.isLockedSubject$.value?.lockedBy?.id === this.appState.userId;
  }

  private _offerTypeSortOrder = {
    [OfferType.MainAssortment.toString()]: 1,
    [OfferType.Campaign.toString()]: 2,
    [OfferType.PriceChanges.toString()]: 3,
    [OfferType.Logistics.toString()]: 4
  };

  public get productInstructions(): string[] {
    if (this.form?.value.offerType?.id == OfferType.Logistics) {
      return ["cards.offer.products.info.text1"];
    }

    let res = [
      "cards.offer.products.info.text1",
      "cards.offer.products.info.text2"
    ];

    if (this.retailerHasRPim) {
      res.push("cards.offer.products.info.remove_or_replace_text");
    }

    if (
      this.campaignType == CalendarGroupCampaignType.RetailSalesPriceDiscount
    ) {
      res.push("cards.offer.products.info.campaign_with_types_disclaimer");
    }

    return res;
  }

  public form: FormGroup;
  public edit = false;

  private _offerCard: IOfferCard;
  private _formData: IOfferCardFormData;
  private _snapshot: any;

  public retailers: IRetailerOption[];
  public period: string;

  public offer: IOfferCardDetail;
  public clientId: string;

  public actionLoading = {} as any;
  public updatingPrices = false;

  public sortOrderDefined = false;

  public recipientsOptions: IOfferRecipient[] = [];
  public assistantsOptions: IUserAssistantDto[] = [];
  public retailerCampaigns: IClvDto[] = [];

  private _hubConnection: HubConnection;
  private _signalRSubscription: Subscription;

  private _offerTitleChangeSubscription: Subscription;
  private _procurementProductsMapper: Subscription;
  private _isProductsPdfDisabledSubscription: Subscription;
  private _isLockedMessageSubscription: Subscription;

  public ownerOptions: IUser[] = [];
  public currencyOptions: IClvDto[] = [];

  public offerTypes: IClvDto[];

  public attachments: IAttachment[];

  public procurementProductsFormGroup: FormGroup = new FormGroup({});

  public supplierAssistantOptions: IOfferAssistantDto[] = [];
  public assistantNotificationTypeOptions = asArray(OfferAssistantNotificationType);

  @ViewChild("filesList") filesList: FilesListComponent;

  public minDate = moment();
  public offerLastSentDate: moment.Moment;
  public offerLastModifiedDate: moment.Moment;

  constructor(
    @Host() private _page: OfferLayoutComponent,
    private _cd: ChangeDetectorRef,
    private _router: Router,
    private _route: ActivatedRoute,
    private _routeNavigator: RouteNavigator,
    private _dateHandler: DateHandler,
    private _offerService: OfferService,
    private _dialog: LxmDialog,
    private _message: LxmMessage,
    public appState: AppState,
    private _authService: AuthenticationService,
    private _clientsService: ClientsService,
    private _signalRService: SignalRService,
    private _translateService: TranslateService,
    private _pipedriveService: PipedriveService,
    private _translatedValuePipe: TranslatedValuePipe,
    private _titleService: TitleService
  ) {
    this._offerCard = _route.snapshot.data.offerCard as IOfferCard;
    this.clientId = _route.snapshot.params?.clientId;

    const { changes, lock } = this._offerCard || {};

    if (changes?.length) {
      const offerLastModifiedDate = changes.find(
        (x: any) => x.type === OfferChangeType.Saved
      )?.createdAt;
      const offerLastSentDate = changes.find(
        (x: any) => x.type === OfferChangeType.SentToRetailer
      )?.createdAt;

      if (offerLastModifiedDate) {
        this.offerLastModifiedDate = moment(offerLastModifiedDate);
      }
      if (offerLastSentDate) {
        this.offerLastSentDate = moment(offerLastSentDate);
      }
    }

    this._formData = this._offerCard?.formData || _route.snapshot.data.formData;
    this.retailers = this._formData.retailers;

    this.offer = this._offerCard?.offerDetail as IOfferCardDetail;
    this.ownerOptions = this._formData.ownerOptions;
    this.supplierAssistantOptions = this._formatRecipientOptions(this._formData.assistantOptions);

    this.attachments = this._offerCard?.attachments || [];

    // this._isLockedMessageSubscription = this.isLockedSubject$.subscribe((x) => {
    //   if (x && !this.isLockedByCurrentUser) {
    //     this._message.openMessage(
    //       {
    //         title: this._translateService.instant(
    //           "cards.offer.messages.offer_locked.title"
    //         ),
    //         message: this._translateService.instant(
    //           "cards.offer.messages.offer_locked.text", { lockedBy: x.lockedBy?.name}
    //         )
    //       },
    //       MessageType.warning
    //     );
    //   }
    // });

    if (lock?.offerId == this.offer?.id) {
      this.isLockedSubject$.next(lock);
    }

    const selectedOfferDetailRetailer = this.offer?.retailer; // selectedRetailer does not contain procurements
    const selectedRetailerId = selectedOfferDetailRetailer?.id;
    let selectedRetailer = selectedRetailerId
      ? this.retailers.find((x) => x.id == selectedRetailerId)
      : null;

    const selectedProcurement = this.offer?.procurement;
    const procurements =
      selectedOfferDetailRetailer?.procurements ||
      (selectedProcurement ? [selectedProcurement] : []);

    this.procurements = procurements;

    const selectedCalendarCampaign = this.offer?.calendarCampaign;
    const calendarCampaigns =
      selectedOfferDetailRetailer?.calendarCampaigns ||
      (selectedCalendarCampaign ? [selectedCalendarCampaign] : []);

    this.calendarCampaigns = calendarCampaigns;

    if (this.offer) {
      this.offerId = this.offer.id;

      this._signalRSubscription = this._signalRService.commonHub.subscribe(
        (connection) => {
          this._hubConnection = connection;
          if (connection?.state === HubConnectionState.Connected) {
            if (this._offerCard?.offerKind === OfferKind.SupplierOffer) {
              connection.send("viewSupplierOffer", this.offer.id);
            } else if (
              this._offerCard?.offerKind === OfferKind.ReceivingOffer
            ) {
              connection.send("viewReceivingOffer", this.offer.id);
            }

            connection.on("statusChanged", (data: any) => {
              this.offer.status = data.status;
              this._cd.detectChanges();
            });

            connection.on("offerHasChanged", (data) => {
              if (this.offerId === data.offerId && data.read === false) {
                this._msgRef = this._message.ok({
                  message: "cards.offer.base_info.offer_changed",
                  indefinite: true,
                  buttonText: "action.reload_page",
                  buttonIcon: "generate",
                  buttonAction: () => {
                    this._router.routeReuseStrategy.shouldReuseRoute = () =>
                      false;
                    this._router.onSameUrlNavigation = "reload";
                    this._router.navigate(["/offers", this.offerId]);
                    this._msgRef.dismiss();
                  }
                });
              }
              this._cd.detectChanges();
            });

            connection.on("offerLocked", (data: any) => {
              this.isLockedSubject$.next(data);
              this._cd.detectChanges();
            });

            connection.on("offerUnlocked", (data: any) => {
              this.isLockedSubject$.next(null);
              this._cd.detectChanges();
            });
          }
        }
      );
    } else if (this.clientId) {
      const predefinedRetailer = this.retailers.find(
        (x) => x.id === this.clientId
      );
      if (predefinedRetailer) {
        selectedRetailer = predefinedRetailer;
      }
    }

    const selectedRecipients = selectedRetailer?.purchaseManagers
      ?.filter(x => this.offer?.recipients
        ?.map(r => r.userId)
        .includes(x.userId)
      );

    this.form = new FormGroup({
      name: new FormControl(this.offer?.name),
      retailer: new FormControl(selectedRetailer),
      currency: new FormControl(this.offer?.currency),
      recipients: new FormControl(
        this._formatRecipientOptions(this.offer?.recipients)
      ),
      assistants: new FormControl(
        this._formatAssistantsOptions(this.offer?.assistants)
      ),
      offerType: new FormControl(),
      retailerCampaign: new FormControl(this.offer?.retailerCampaign),
      dateFrom: new FormControl(this.offer?.from?.editableDate),
      dateTo: new FormControl(this.offer?.to?.editableDate),
      description: new FormControl(this.offer?.description),
      products: new FormControl(
        this._offerCard?.products ||
        this._formData.products?.map((x) =>
          Object.assign({}, x, { id: null, isNew: true })
        ) ||
        []
      ),
      offerOwner: new FormControl(this._offerCard?.owner),
      procurement: new FormControl(selectedProcurement),
      includeProductsPdf: new FormControl(
        this.offer?.includeProductsPdf ?? false
      ),
      includeConformityDeclarationsPdf: new FormControl(
        this.offer?.includeConformityDeclarationsPdf ?? false
      ),
      canOrder: new FormControl(
        this.offer?.canOrder ?? this.canCreateSalesOrders
      ),
      calendarCampaign: new FormControl(selectedCalendarCampaign),
      addSupplierAssistant: new FormControl(),
      supplierAssistants: new FormControl(),
      assistantNotificationType: new FormControl()
    });

    this.updateSupplierAssistants();
    this._updateCurrencyOptions(selectedRetailer);
    this._updateRecipientsOptions(selectedRetailer);
    this._updateRetailerCampaigns(selectedRetailer);
    this._updateOfferTypes(selectedRetailer);
    this._updateAssistantsOptions(selectedRecipients);

    if (this.procurements?.length) {
      this._updateProcurements(procurements, selectedProcurement);
    }

    if (calendarCampaigns?.length) {
      this._updateCalendarCampaigns(
        null,
        calendarCampaigns,
        selectedCalendarCampaign
      );
    }

    this.form
      .get("offerType")
      .setValue(
        this.offer?.offerType ||
        this.offerTypes.find(
          (x) => x.id === OfferType.MainAssortment.toString()
        ),
        {
          emitEvent: false
        }
      );

    this.form.get("retailer").valueChanges.subscribe((val) => {
      this._updateCurrencyOptions(val);
      this._updateRecipientsOptions(val);
      this._updateAssistantsOptions([]);

      this.form.get("recipients").setValue([]);
      this.form.get("assistants").setValue([]);

      this._updateRetailerCampaigns(val);
      this.form.get("retailerCampaign").setValue(null);

      this._updateOfferTypes(val);
      this._updateCalendarCampaigns([], [], null);
      this._cd.detectChanges();
    });

    this.form.get("recipients").valueChanges.subscribe((val) => {
      this._updateAssistantsOptions(val);
      this.form.get("assistants").setValue(this.assistantsOptions);
      this._cd.detectChanges();
    });

    this.form.get("offerType").valueChanges.subscribe((val) => {
      const retailerId = this.form.value.retailer?.id;
      if (val?.id == OfferType.Procurement && retailerId) {
        this._offerService
          .getActiveProcurements(retailerId)
          .subscribe((procurements) => {
            this._updateProcurements(procurements);
          });
      } else if (val?.id == OfferType.Campaign && retailerId) {
        this._offerService.getActiveCampaigns(retailerId).subscribe((res) => {
          this._updateCalendarCampaigns(
            res.retailerCampaignTypes,
            res.activeCalendarCampaigns
          );
        });
      }
      this._cd.detectChanges();
    });

    this.form.get("procurement").valueChanges.subscribe((val) => {
      this._setProcurementControls(val, this.form.get("products").value);
      this._cd.detectChanges();
    });

    this.form.get("calendarCampaign").valueChanges.subscribe((x) => {
      if (!x) {
        return;
      }

      const newName = this._translatedValuePipe.transform(x?.name);
      const newValidFrom = moment(x?.validFrom);
      const newValidTo = moment(x?.validTo);

      var patch = {
        name: newName,
        dateFrom: newValidFrom,
        dateTo: newValidTo
      };

      this.form.patchValue(patch, { emitEvent: false });
      this._cd.detectChanges();
    });
  }

  public get calendarEntryIdFromParams() {
    const calendarEntryIdFromParams =
      this._route.snapshot.params?.calendarEntryId;
    if (calendarEntryIdFromParams) {
      return calendarEntryIdFromParams;
    }
  }

  public get calendarEntryId() {
    const offerCalendarEntryId = this.offer?.calendarEntryId;
    if (offerCalendarEntryId) {
      return offerCalendarEntryId;
    }

    const calendarEntryIdFromParams =
      this._route.snapshot.params?.calendarEntryId;
    if (calendarEntryIdFromParams) {
      return calendarEntryIdFromParams;
    }

    return null;
  }

  public get offerStatus() {
    return this.offer?.status;
  }

  public get canDelete() {
    if (!this.offer) {
      return false;
    }

    if (
      this.offerStatus === SupplierOfferStatus.Created ||
      this.offerStatus === SupplierOfferStatus.Expired ||
      this.offerStatus === SupplierOfferStatus.Rejected
    ) {
      return true;
    }

    return false;
  }

  public get canAccept() {
    if (!this.offer) {
      return false;
    }

    if (!this._authService.hasRight([UserAction.ConfirmSupplierOffers])) {
      return false;
    }

    if (
      this.offerStatus === SupplierOfferStatus.Sent ||
      this.offerStatus === SupplierOfferStatus.Negotiating
    ) {
      return true;
    }

    return false;
  }

  public get canChangeProcurement() {
    if (!this.offer) {
      return true;
    }

    if (this.offerStatus === SupplierOfferStatus.Created) {
      return true;
    }

    if (!this.offer.procurement) {
      return true;
    }

    return false;
  }

  public get canChangeCampaignCalendarEntry() {
    if (!this.offer) {
      return true;
    }

    if (this.offerStatus === SupplierOfferStatus.Created) {
      return true;
    }

    if (!this.offer.calendarCampaign) {
      return true;
    }

    return false;
  }

  public get isOfferModifiedAfterSent() {
    if (!this.offerLastSentDate || !this.offerLastModifiedDate) {
      return false;
    }
    return this.offerLastModifiedDate.isAfter(this.offerLastSentDate);
  }

  public get offerType() {
    return this.form.get("offerType").value;
  }

  public get offerTypeId() {
    return this.form.get("offerType").value?.id;
  }

  public get isRequestedOffer(): boolean {
    return this.offer?.isRequestedOffer || false;
  }

  public get canEditOfferType(): boolean {
    if (!this.edit || this.isRequestedOffer || this.isFromCalendarEntry) {
      return false;
    }

    return (
      (this.offer?.status ?? SupplierOfferStatus.Created) ==
      SupplierOfferStatus.Created || this.offer?.status === 0
    );
  }

  public get isFromCalendarEntry(): boolean {
    return this.calendarEntryId ? true : false;
  }

  public get canEditRetailer(): boolean {
    if (
      this.isRequestedOffer ||
      this.isFromCalendarEntry ||
      this.hasPredefinedRetailer
    ) {
      return false;
    }

    return (
      (this.offer?.status ?? SupplierOfferStatus.Created) ==
      SupplierOfferStatus.Created || this.offer?.status === 0
    );
  }

  public get canEditCurrency(): boolean {
    if (this.currencyOptions.length < 2) {
      return false;
    }

    return (
      (this.offer?.status ?? SupplierOfferStatus.Created) ==
      SupplierOfferStatus.Created || this.offer?.status === 0
    );
  }

  public get canEditPeriod(): boolean {
    return (
      this.edit &&
      !this.isFromCalendarEntry &&
      !this.form.get("calendarCampaign").value
    );
  }

  public get hasExportFilesFromDifferentGs1Groups(): boolean {
    return this._offerCard?.hasExportFilesFromDifferentGs1Groups;
  }

  public set hasExportFilesFromDifferentGs1Groups(value: boolean) {
    if (!this._offerCard) return;
    this._offerCard.hasExportFilesFromDifferentGs1Groups = value;
  }

  public get selectedRecipients() {
    return this.form.get("recipients").value;
  }

  public get selectedAssistants() {
    return this.form.get("assistants").value;
  }

  public get selectedSupplierAssistants() {
    return this.form.get("supplierAssistants").value;
  }

  public get isCustomRecipientDisabled() {
    const retailer = this.form.get("retailer").value;
    return retailer?.isCustomRecipientForOfferDisabled || false;
  }

  public get hasAutomaticConfirmation() {
    const retailer = this.form.get("retailer").value;
    return retailer?.hasAutomaticOfferConfirmation || false;
  }

  public get isSupplierOfferConfirmDisabled() {
    const retailer = this.form.get("retailer").value;
    return retailer?.isSupplierOfferConfirmDisabled || false;
  }

  public get canCreateSalesOrders() {
    return this._authService.hasModule(LxmAppModule.CreateSalesOrders);
  }

  public get isOfferTypeOrderable() {
    return ORDERABLE_OFFER_TYPES.includes(this.offerTypeId);
  }

  public get canEdit() {
    return (
      this.isEditable &&
      this._authService.hasRight([UserAction.ManageSupplierOffers])
    );
  }

  public get offerTitle() {
    const offerName = this.form.get("name").value;

    if (typeof offerName === "string" && offerName.length > 0) {
      return offerName;
    }

    if (!this.offerId) {
      return this._translateService.instant(
        this.translationsPath + ".title_new_offer"
      );
    } else {
      return this._translateService.instant(
        this.translationsPath + ".title_edit_offer"
      );
    }
  }

  public get isProSelected() {
    return this.form.get("retailer").value?.pro;
  }

  public get isQuotaExceeded() {
    return this._offerCard?.isQuotaExceeded ?? false;
  }

  public get retailerHasRPim() {
    return this.form.get("retailer").value?.hasRPim;
  }

  public get isExportXlsEnabled() {
    const retailer = this.form.get("retailer").value;
    if (retailer.isClient) {
      const xlsExportTypes = [
        OfferExportType.LxmCrmOfferEng,
        OfferExportType.LxmCrmOfferEst,
        OfferExportType.LxmCrmOfferSimpleEng,
        OfferExportType.LxmCrmOfferSimpleEst,
        OfferExportType.SupplierProductsExport
      ];
      return xlsExportTypes.includes(retailer.offerExportType);
    } else {
      return true;
    }
  }

  public get isExportPdfEnabled() {
    const retailer = this.form.get("retailer").value;
    if (retailer.isClient) {
      const pdfExportTypes = [
        OfferExportType.OfferPdf,
        OfferExportType.OfferAndProductsPdf
      ];
      return pdfExportTypes.includes(retailer.offerExportType);
    } else {
      return this.appState.hasModule(LxmAppModule.Pdf);
    }
  }

  public get isExportProductsPdfEnabled() {
    return this.offer?.status !== this.OfferStatus.Created
      && this.form.get('includeProductsPdf').value;
  }

  public get procurement() {
    return this.form.get("procurement").value;
  }

  public get isProcurement() {
    return this.offerTypeId === OfferType.Procurement;
  }

  public get offerDateFrom() {
    if (this.isProcurement) {
      return this.procurement?.validFrom;
    }
    return this.form.get("dateFrom").value;
  }

  public get offerSupplyPeriodFrom() {
    return this.procurement?.supplyFrom;
  }

  public get offerSupplyPeriodTo() {
    return this.procurement?.supplyTo;
  }

  public get procurementDescription() {
    return this._translatedValuePipe.transform(this.procurement?.description);
  }

  public get offerDescription() {
    return this.form.get("description").value;
  }

  public get offerDateTo() {
    if (this.offerTypeId === OfferType.Procurement) {
      return this.procurement?.validTo;
    }
    return this.form.get("dateTo").value;
  }

  public get offerProducts() {
    return this._offerCard?.products;
  }

  public get hasProducts() {
    return this.form.get("products").value?.length > 0;
  }

  public get hasPredefinedRetailer() {
    return this.clientId && this.form.get("retailer").value;
  }

  public get offerCurrency() {
    return this.form.get("currency").value;
  }

  public get canIncludeProductsPdf() {
    return (
      this.appState.hasModule(LxmAppModule.Pdf) &&
      this.form.get("retailer").value?.offerExportType !=
      OfferExportType.OfferAndProductsPdf
    );
  }

  public get canIncludeConformityDeclarationsPdf() {
    return this.appState.hasModule(LxmAppModule.ConformityDeclarations) &&
      this.appState.hasRights([UserAction.ManageConformityDeclarations]) &&
      this._formData.hasConformityDeclarationAttachmentOption;
  }

  public get selectedRetailer() {
    return this.form.get("retailer").value;
  }

  public get showOfferDateFromField() {
    return this.form.value.offerType?.id == OfferType.PriceChanges;
  }

  public get showOfferSupplyPeriodField() {
    return (
      this.isProcurement &&
      (this.offerSupplyPeriodFrom || this.offerSupplyPeriodTo)
    );
  }

  public get showOfferPeriodField() {
    return (
      this.form.value.offerType?.id != OfferType.Logistics &&
      !this.showOfferDateFromField
    );
  }

  public get showAddNewContactButton() {
    const hasModule = this._authService.hasModule(LxmAppModule.SellerCrm);
    const hasRight = this._authService.hasRight([
      UserAction.ManageClients,
      UserAction.ManageOthersClients
    ]);
    return hasModule && hasRight && this.selectedRetailer?.isClient;
  }

  public get isCampaign() {
    return this.offerTypeId === OfferType.Campaign;
  }

  public get campaignType() {
    return (
      this.form.get("calendarCampaign").value?.campaignType ??
      CalendarGroupCampaignType.BuyInPriceDiscount
    );
  }

  public get campaignTypes() {
    const selectedCalendarCampaign = this.form.get("calendarCampaign").value;
    const formData =
      this._route.snapshot.data.formData ??
      this._route.snapshot.data.offerCard?.formData;

    if (selectedCalendarCampaign?.campaignTypes.length) {
      return selectedCalendarCampaign.campaignTypes;
    }

    return formData?.campaignTypes || [];
  }

  public sortClient(a: IRetailerOption, b: IRetailerOption): number {
    return (a.pro ? 1 : 0) + a.name > (b.pro ? 1 : 0) + b.name ? 1 : -1;
  }

  public onOwnerChange($event) {
    this._offerService.setOwner(this.offerId, $event.id).subscribe(
      (res) => {
        this._message.ok({
          message: "cards.offer.base_info.owner_set_successfully"
        });
      },
      (err) => {
        this._message.error({
          message: "cards.offer.base_info.setting_owner_failed"
        });
      }
    );
  }

  private _updateCalendarCampaigns(
    retailerCampaignTypes: ICampaignTypeDto[],
    calendarCampaigns: IOfferCardCalendarCampaignDetail[] = this
      .calendarCampaigns,
    selected: IOfferCardCalendarCampaignDetail = this.form.get(
      "calendarCampaign"
    ).value
  ) {
    if (retailerCampaignTypes != null) {
      if (this._route.snapshot.data.formData) {
        this._route.snapshot.data.formData.campaignTypes =
          retailerCampaignTypes;
      }

      if (this._route.snapshot.data.offerCard?.formData) {
        this._route.snapshot.data.offerCard.formData.campaignTypes =
          retailerCampaignTypes;
      }
    }

    const selection = selected || null;
    this.calendarCampaigns = calendarCampaigns;
    this.form.get("calendarCampaign").setValue(selection);
  }

  private _updateProcurements(
    procurements = this.procurements,
    selected = this.form.get("procurement").value
  ) {
    if (!procurements.length) {
      return;
    }

    const selection = selected || procurements[0];
    this._setProcurementControls(selection, this.form.get("products").value);
    this.procurements = procurements;
    this.form.get("procurement").setValue(selection);

    if (selection.canOrder) {
      this.form.get("canOrder").setValue(true);
    }
  }

  private _setProcurementControls(procurement, products?) {
    if (this._procurementProductsMapper) {
      this._procurementProductsMapper.unsubscribe();
    }
    const fg = new FormGroup({});

    if (procurement?.items.length) {
      for (let i = 0; i < procurement.items.length; i++) {
        const procurementItemId = procurement.items[i].id;
        const controlProducts = products?.length
          ? products.filter((x) => x.procurementItemId === procurementItemId)
          : [];
        fg.addControl(procurementItemId, new FormControl(controlProducts));
      }
    }

    this._procurementProductsMapper = fg.valueChanges.subscribe((val) => {
      const mappedProducts = [].concat.apply(
        [],
        Object.keys(val).map((x) => {
          let products = val[x];
          for (let product of products) {
            product.procurementItemId = x;
          }
          return products;
        })
      );

      this.form.get("products").setValue(mappedProducts, { emitEvent: false });
    });

    this.procurementProductsFormGroup = fg;
  }

  private updateSupplierAssistants() {
    const defaultSupplierAssistants = this.supplierAssistantOptions.filter(x => x.isDefault);
    const supplierAssistantsPatch = {
      addSupplierAssistant: false,
      supplierAssistants: defaultSupplierAssistants,
      assistantNotificationType: defaultSupplierAssistants?.length
        ? this.assistantNotificationTypeOptions.find(x => x.id == defaultSupplierAssistants[0].notificationType)
        : this.assistantNotificationTypeOptions[0]
    };

    if (this.offer && this.offer.supplierAssistants?.length) {
      supplierAssistantsPatch.supplierAssistants = this._formatRecipientOptions(this.offer.supplierAssistants);
      supplierAssistantsPatch.assistantNotificationType = this.assistantNotificationTypeOptions.find(x => x.id == supplierAssistantsPatch.supplierAssistants[0].notificationType);
      supplierAssistantsPatch.addSupplierAssistant = true;
    }

    this.form.patchValue(supplierAssistantsPatch, { emitEvent: false });
  }

  private _updateCurrencyOptions(selectedRetailer: IRetailerOption) {
    if (!selectedRetailer) {
      this.currencyOptions = [];
      this.form.get("currency").setValue(null);
      return;
    }

    if (selectedRetailer.isClient) {
      this.currencyOptions = this._formData.currencies;
    } else {
      this.currencyOptions = this._formData.currencies.filter(
        (x) => selectedRetailer.acceptedCurrencies?.indexOf(x.value) >= 0
      );
    }

    const currentValue = this.form.get("currency").value?.id;
    this.form
      .get("currency")
      .setValue(
        this.currencyOptions.find((x) => x.id === currentValue) ||
        this.currencyOptions.find(
          (x) => x.value == selectedRetailer.defaultCurrency
        )
      );
  }

  private _updateRecipientsOptions(
    selectedRetailer: IRetailerOption = this.selectedRetailer
  ) {
    const retailerId = selectedRetailer?.id;
    this.recipientsOptions = !retailerId
      ? []
      : this._formatRecipientOptions(
        this.retailers.find((x) => x.id === retailerId)?.purchaseManagers
      );
  }

  private _updateAssistantsOptions(
    selectedPurchaseManagers: IOfferRecipient[]
  ) {
    if (selectedPurchaseManagers) {
      let managersAssistants = this.getAllAssistants(selectedPurchaseManagers);
      this.assistantsOptions = !managersAssistants
        ? []
        : this._formatAssistantsOptions(managersAssistants);
    } else {
      this.assistantsOptions = [];
    }
  }

  private getAllAssistants(selectedPurchaseManagers: IOfferRecipient[]) {
    const managerIds = selectedPurchaseManagers.map(x => x.userId).filter(x => x != null);

    if (selectedPurchaseManagers) {
      let managersAssistants = [];
      selectedPurchaseManagers.forEach((manager) => {
        if (manager.assistants) {
          manager.assistants.forEach((assistant) => {
            if (!managerIds.includes(assistant.id) && !managersAssistants.some(m => m.id == assistant.id)) {
              managersAssistants.push(assistant);
            }
          });
        }
      });

      return managersAssistants;
    }

    return [];
  }

  private _updateRetailerCampaigns(selectedRetailer: IRetailerOption) {
    const retailerId = selectedRetailer?.id;
    this.retailerCampaigns = !retailerId
      ? []
      : this.retailers.find((x) => x.id === retailerId)?.campaigns || [];
  }

  private _updateOfferTypes(selectedRetailer: IRetailerOption) {
    if (!selectedRetailer) {
      this.offerTypes = this._formData.offerTypes.sort((a, b) =>
        this._sortOfferTypeFn(a, b)
      );
      return;
    }

    let offerTypes = this._formData.offerTypes
      .filter((x) => selectedRetailer.offerTypes.includes(x.id))
      .sort((a, b) => this._sortOfferTypeFn(a, b));

    if (selectedRetailer.isClient) {
      offerTypes = offerTypes.filter((x: any) => x.id !== OfferType.Campaign);
    }

    this.offerTypes = offerTypes;

    const selectedOfferTypeId = this.form.value.offerType?.id;
    if (!selectedRetailer.offerTypes.includes(selectedOfferTypeId)) {
      this.form.get("offerType").setValue(this.offerTypes[0]);
    } else if (this.offerTypeId === OfferType.Procurement) {
      this.form.get("offerType").setValue(this.offerType); // Trigger subscriber
    }
  }

  private _sortOfferTypeFn(a, b) {
    if (this._offerTypeSortOrder[a.id] < this._offerTypeSortOrder[b.id]) {
      return -1;
    }
    if (this._offerTypeSortOrder[a.id] > this._offerTypeSortOrder[b.id]) {
      return 1;
    }
    return 0;
  }

  private _formatRecipientOptions(options: any[]) {
    if (!options) {
      return [];
    }
    return options.map((x) =>
      Object.assign({}, x, {
        label: x.name
          ? x.email
            ? `${x.name} (${x.email})`
            : `${x.name} (${this._translateService.instant("global.no_email")})`
          : x.email
      })
    );
  }


  public getTranslatedAssistantPortfolio(assistant: IUserAssistantDto) {
    const portfolio = assistant?.portfolio?.filter(x => !stringIsNullOrEmpty(x.value));

    if (portfolio && portfolio.length > 0) {
      return this._translatedValuePipe.transform(portfolio);
    }

    return "";
  }

  private _formatAssistantsOptions(options: any[]) {
    if (!options) {
      return [];
    }

    return options.map((x): any => {
      const portfolio = this.getTranslatedAssistantPortfolio(x);
      return Object.assign({}, x, {
        label: portfolio
          ? `${x.fullName} (${portfolio})`
          : x.fullName
      });
    });
  }

  private _setOfferTitle() {
    const form = this.form.value;
    const { retailer } = form;
    if (this.offerTitle) {
      const retailerShortName = retailer?.shortName;
      const title = retailerShortName
        ? retailerShortName + " - " + this.offerTitle
        : this.offerTitle;
      this._titleService.setTitle(title);
    }
  }

  private _checkCanIncludePdf() {
    const statuses = [OfferProductStatus.Rejected, OfferProductStatus.Removed];
    const products = this.form.get("products").value;
    const activeProducts = products.filter(x => !statuses.includes(x.status));
    const replacementProducts = products.filter(x => x.replacementForProductId != null);

    if ((activeProducts.length - replacementProducts.length) > 25) {
      this.includeProductsPdfDisabled = true;
      this.includeConformityDeclarationsPdfDisabled = true;
      this.form.get("includeProductsPdf").setValue(false);
      this.form.get("includeConformityDeclarationsPdf").setValue(false);
    } else {
      this.includeProductsPdfDisabled = false;
      this.includeConformityDeclarationsPdfDisabled = false;
    }
  }

  ngOnInit() {
    if (!this.offerId) {
      this.edit = true;
      this.editing.emit(this.edit);
    }
    this._snapshot = this.form.value;
    merge(
      this.form.get("dateFrom").valueChanges,
      this.form.get("dateTo").valueChanges
    ).subscribe(() => {
      const begin = this.form.get("dateFrom").value;
      const end = this.form.get("dateTo").value;
      const period = this._dateHandler.rangePeriodChecker(begin, end);
      this.period = period;
    });

    this.form.valueChanges.subscribe((x) => {
      this.onFormChange.emit(x);
    });

    // this._pipedriveService.appendLeadbooster(
    //   "e15ac60b-3def-4c17-9dc3-0517e884b88b"
    // );

    this._setOfferTitle();
    this._offerTitleChangeSubscription = merge(
      this.form.get("retailer").valueChanges,
      this.form.get("name").valueChanges
    )
      .pipe(debounceTime(200))
      .subscribe((val) => {
        this._setOfferTitle();
      });

    this._checkCanIncludePdf();

    this._isProductsPdfDisabledSubscription = this.form
      .get("products")
      .valueChanges.subscribe((val) => {
        this._checkCanIncludePdf();
      });
  }

  public setDatePeriod(period: string) {
    const dateFromControl = this.form.get("dateFrom");
    const dateToControl = this.form.get("dateTo");

    const x = this._dateHandler.getPeriod(period, dateFromControl.value);
    dateFromControl.setValue(x.begin);
    dateToControl.setValue(x.end);
    this.period = period;
  }

  public openImportProductDialog() {
    this._dialog.open(
      SupplierOfferImportProductDialog,
      {
        data: {
          offerId: this.offerId,
          offerType: this.offerType
        }
      },
      (res) => {
        if (res) {
          this.save(() => {
            window.location.reload();
          });
        }
      }
    );
  }

  public backToList() {
    this._routeNavigator.toPreviousRoute({ fallback: "/offers" });
  }

  public exportXls() {
    this._dialog.open(OfferExportDialogComponent, {
      resolve: () =>
        this._offerService
          .exportXls(this.offerId)
          .pipe(
            mergeMap(async (res) => {
              const blob = await this._offerService
                .downloadXls(res.id)
                .toPromise();
              return {
                fileName: res.fileName,
                blob: blob
              };
            })
          )
          .pipe(
            map((res) => {
              const url = window.URL.createObjectURL(res.blob);

              const link = this._download.nativeElement;
              link.href = url;
              link.download = res.fileName;
              link.click();

              window.URL.revokeObjectURL(url);
            })
          )
    });
  }

  public exportPdf() {
    this.exportPdfInternal(this._offerService.exportPdf(this.offerId));
  }

  public exportProductsPdf() {
    let request: Observable<any>;

    if (this._offerCard?.offerKind === OfferKind.SupplierOffer) {
      request = this._offerService.exportSupplierOfferProductsPdf(this.offerId);
    } else if (this._offerCard?.offerKind === OfferKind.ReceivingOffer) {
      request = this._offerService.exportReceivingOfferProductsPdf(this.offerId);
    }

    if (!request) {
      return;
    }

    this.exportPdfInternal(request);
  }

  private exportPdfInternal(request: Observable<any>) {
    this._dialog.open(OfferExportDialogComponent, {
      resolve: () =>
        request
          .pipe(
            catchError((err) => {
              this._message.error({
                message: "global.errors.generic_error"
              });
              return EMPTY;
            }),

            mergeMap(async (res) => {
              const blob = await this._offerService
                .downloadPdf(res.id)
                .toPromise();
              return {
                fileName: res.fileName,
                blob: blob
              };
            })
          )
          .pipe(
            map((res) => {
              const url = window.URL.createObjectURL(res.blob);

              const link = this._download.nativeElement;
              link.href = url;
              link.download = res.fileName;
              link.click();

              window.URL.revokeObjectURL(url);
            })
          )
    });
  }

  public clone() {
    this._router.navigate(["/offers/clone", this.offerId]);
  }

  public async toggleEdit() {
    if (this.edit) {
      this._dialog.confirm(
        {
          message: "cards.offers.offer_dialogs.discard_changes.card_text",
          title: "cards.offers.offer_dialogs.discard_changes.title"
        },
        async (res) => {
          await this._offerService.unlock(this.offerId).toPromise();
          this._revertToInitialData();
          this.edit = !this.edit;
          this.editing.emit(this.edit);
        }
      );
    } else {
      await this._offerService.lock(this.offerId).toPromise();
      this._saveInitialData();
      this.edit = !this.edit;
      this.editing.emit(this.edit);
    }
  }

  public getOfferStatusProgress(status: SupplierOfferStatus) {
    return this._offerService.getSendingOfferStatusProgress(status);
  }

  private _productMapper(x, index) {
    let product = {
      id: x.id,
      status: x.status,
      productId: x.productId,
      display: x.display || false,
      availableFrom: x.availableFrom,
      salePrice: x.salePrice,
      suggestedRetailPrice: x.suggestedRetailPrice,
      listPrice: null,
      sortOrderDefined: this.sortOrderDefined,
      sortOrder: index,
      orderDetails: x.orderDetails,
      replacementForProductId: x.replacementForProductId,
      isSuspended: x.isSuspended
    };

    switch (this.offerTypeId) {
      case OfferType.PriceChanges:
        Object.assign(product, {
          priceChangeDate: x.priceChangeDate,
          priceChangeReasonId: x.priceChangeReason?.id,
          priceChangeNote: x.priceChangeNote,
          salePrice: x.salePricePriceChanges
            ? x.salePricePriceChanges
            : x.salePrice,
          listPrice: x.editableListPrice
        });
        break;
      case OfferType.Procurement:
        Object.assign(product, {
          procurementItemId: x.procurementItemId,
          procurementDetails: x.procurementDetails
        });
        break;
      case OfferType.Campaign:
        Object.assign(product, {
          salePrice: x.salePriceCampaign ? x.salePriceCampaign : x.salePrice,
          campaignTypeId:
            this.campaignType ==
              CalendarGroupCampaignType.RetailSalesPriceDiscount
              ? x.campaignType?.id
              : null,
          campaignInfo: x.campaignInfo
            ? {
              limit: x.campaignInfo.limit,
              netting: x.campaignInfo.netting || false,
              addressed: x.campaignInfo.addressed || false,
              withAd: x.campaignInfo.withAd || false,
              marketingFee: x.campaignInfo.marketingFee,
              invoiceRecipientId: x.campaignInfo.invoiceRecipient?.id,
              storeCount: x.campaignInfo.storeCount,
              displaySize: x.campaignInfo.displaySize,
              returnableItems: x.campaignInfo.returnableItems,
              comments: x.campaignInfo.comments,
              campaignNr: x.campaignInfo.campaignNr,
              campaignPriceValidFrom: x.campaignPriceValidFrom
            }
            : null
        });
        break;
    }

    return product;
  }

  private _serializeSaveData(f = this.form.value) {
    let data: any = {
      retailerId: f.retailer?.id,
      currencyId: f.currency?.id,
      recipients: f.recipients,
      assistants: f.assistants,
      offerTypeId: f.offerType?.id,
      retailerCampaignId: f.retailerCampaign?.id,
      name: f.name,
      description: f.description,
      validFrom: f.dateFrom,
      validTo: f.dateTo,
      canOrder: this.isOfferTypeOrderable ? f.canOrder : false,
      offerCalendarEntryId: this.calendarEntryId,
      supplierAssistants: f.addSupplierAssistant
        ? f.supplierAssistants
          .map((x) => ({
            id: x.id,
            notificationType: f.assistantNotificationType?.id
          }))
        : null,
      products: this.offer?.status === this.OfferStatus.Created
        ? f.products
          .filter((x) => !x.isDeleted)
          .map((product, index) => this._productMapper(product, index))
        : f.products.map((product, index) =>
          this._productMapper(product, index)
        )
    };

    switch (this.offerTypeId) {
      case OfferType.Procurement:
        Object.assign(data, {
          procurementId: this.procurement?.id
        });
        break;
      case OfferType.PriceChanges:
        data.validTo = null;
        break;
      case OfferType.Campaign:
        Object.assign(data, {
          campaignType: this.campaignType
        });
        break;
    }

    if (this.canIncludeProductsPdf) {
      data.includeProductsPdf = f.products.length > 25 ? false : f.includeProductsPdf;
    }

    if (this.canIncludeConformityDeclarationsPdf) {
      data.includeConformityDeclarationsPdf = f.products.length > 25 ? false : f.includeConformityDeclarationsPdf;
    }

    return data;
  }

  private setDefaultSalePrice() {
    const products = this.form.get("products").value;

    Object.keys(products).forEach((x) => {
      const product = products[x];
      if (isNaN(product.salePrice) || product.salePrice < 0) {
        product.discount = 0;
        product.salePrice = product.listPrice;
      }
    });

    this.form.patchValue({
      products: products
    });
  }

  public save(callback = null) {
    this.setDefaultSalePrice();

    const data = this._serializeSaveData();
    if (callback) {
      data.isImport = true;
    }

    const save = this.offerId
      ? this._offerService.saveOffer(this.offerId, data)
      : this._offerService.createOffer(
        Object.assign({}, data, {
          attachments: this.attachments.map((x) => ({
            name: x.name,
            description: x.description,
            fileId: x.fileId
          }))
        })
      );

    save.result(
      this.form,
      (res) => {
        this.offerLastModifiedDate = moment();

        this._message.ok({
          message: "common.message.offer_saved_successfully"
        });

        if (this.offerId) {
          this._saveInitialData();
        } else {
          this._router.navigate(["/offers", res], { replaceUrl: true });
        }

        this.hasExportFilesFromDifferentGs1Groups =
          res.hasExportFilesFromDifferentGs1Groups;

        this.edit = false;
        this.editing.emit(this.edit);

        if (callback) {
          callback();
        }
      },
      (error) => {
        this._message.error({
          messages: ["common.error.save_failed", error.validationSummary]
        });

        setTimeout((_) => {
          this._cd.detectChanges();
        });
      },
      "save",
      this.actionLoading
    );
  }

  public addNewRecipient = (email) => {
    return new Promise((resolve) => {
      const newRecipient = {
        id: null,
        name: null,
        email: email,
        label: email
      };
      resolve(newRecipient);
    });
  };

  public openAddNewContactDialog() {
    const selectedClientId = this.selectedRetailer?.id;

    if (selectedClientId) {
      this._clientsService.getClient(selectedClientId).result(null, (res) => {
        this._dialog.open(
          AddContactDialogComponent,
          {
            ...appSettings.DIALOG_SIZES.M,
            data: {
              clientId: selectedClientId,
              formData: res?.formData,
              edit: true
            }
          },
          (res) => {
            if (res) {
              const { email, userId, name } = res || {};

              const retailerOptionsRef = this.retailers.find(
                (x) => x.id === selectedClientId
              );

              const newRecipient = {
                id: null,
                userId: userId,
                email: email,
                name: name,
                assistants: []
              };

              if (Array.isArray(retailerOptionsRef?.purchaseManagers)) {
                retailerOptionsRef.purchaseManagers.push(newRecipient);
              } else {
                retailerOptionsRef.purchaseManagers = [newRecipient];
              }

              this._updateRecipientsOptions();
            }
          }
        );
      });
    }
  }

  public isRowInvalid(row: any): boolean {
    return this.productsList.isRowInvalid(row);
  }

  public get isAnyRowInvalid(): boolean {
    return this.form.get("products").value?.some((x) => this.isRowInvalid(x));
  }

  public get sendOfferRequirements() {
    const data = this._serializeSaveData();

    const { products } = data || {};

    const offerHasProducts = products?.length ? true : false;
    const productsHaveSalePrice = offerHasProducts
      ? products.some((x) => isNaN(x.salePrice) || x.salePrice < 0)
        ? false
        : true
      : false;

    const requirements = {
      offerHasProducts: offerHasProducts,
      productsHaveSalePrice: productsHaveSalePrice
    };

    return requirements;
  }

  public openMissingRequirementsDialog(
    requirements = this.sendOfferRequirements
  ) {
    const { offerHasProducts, productsHaveSalePrice } = requirements;

    const noProductsTemplate = `
      <p>
        <span class="bold">${this._translateService.instant("cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.invalid_products_warning")}</span> 
        ${this._translateService.instant("cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.missing_requirements_no_products_text")}</p>
      <p>
    `;

    const noSalePriceTemplate = `
      <p>
        <span class="bold">${this._translateService.instant("cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.invalid_products_warning")}</span> 
        ${this._translateService.instant("cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.missing_requirements_no_sale_price_text")}</p>
      <p>
    `;

    this._dialog.confirm(
      {
        image: "warning-modal-image",
        template: `
      <p class="dialog-title">
        ${this._translateService.instant("cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.missing_requirements_title")}</p>
      <p>
      ${!offerHasProducts ? noProductsTemplate : ""}
      ${offerHasProducts && !productsHaveSalePrice ? noSalePriceTemplate : ""}  
      `,
        yes: "cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.cancel",
        showNo: false
      },
      (res) => {
        return;
      }
    );
  }

  // public get productAcExcludeIds() {
  //   return [];
  //   const { products } = this.form.value;
  //   const uniqueIds = new Set();

  //   for (const product of products) {
  //     uniqueIds.add(product.productId);
  //   }

  //   const ids = Array.from(uniqueIds);
  //   return  ids;
  // }

  public send() {
    const requirements = this.sendOfferRequirements;
    const { offerHasProducts, productsHaveSalePrice } = requirements;

    const { products } = this.form.value;

    const getDuplicateProducts = () => {
      const seen = new Set();
      const duplicates = [];

      for (const product of products) {
        const id = product.productId;
        if (seen.has(id)) {
          duplicates.push(product);
        } else {
          seen.add(id);
        }
      }

      return duplicates;
    };

    const duplicateProducts = getDuplicateProducts();

    if (duplicateProducts.length > 0) {
      const firstDuplicateProduct = duplicateProducts[0];
      this._message.error({
        message: this._translateService.instant(
          "cards.offers.errors.duplicate_product",
          {
            name: this._translatedValuePipe.transform(
              firstDuplicateProduct.name
            ),
            ean: firstDuplicateProduct.ean
          }
        )
      });
      return;
    }

    if (
      this.offerType?.id !== OfferType.Logistics &&
      (!offerHasProducts || !productsHaveSalePrice)
    ) {
      this.openMissingRequirementsDialog(requirements);
      return;
    }

    let confirmOptions = {
      image: "send-proposals-modal-image",
      yes: "cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.confirm",
      no: "cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.cancel",
      template: null,
      swapButtons: false,
      showNo: true,
      showYes: true
    };

    const hasCalendarCampaignSelected =
      this.offerType?.id === OfferType.Campaign &&
        this.campaignType === CalendarGroupCampaignType.RetailSalesPriceDiscount &&
        this.form.value.calendarCampaign?.id
        ? true
        : false;

    let anyProductsMissingCampaignType = false;

    if (hasCalendarCampaignSelected) {
      anyProductsMissingCampaignType = products.some(
        (x) => x.campaignType?.id == null
      );
    }

    if (hasCalendarCampaignSelected && anyProductsMissingCampaignType) {
      confirmOptions.image = "warning-modal-image";
      confirmOptions.showYes = false;
      confirmOptions.template = `
        <p class="dialog-title">${this._translateService.instant("cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.product_no_campaign_type_selected.title")}</p>
        <p>
          <span class="bold">${this._translateService.instant("cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.invalid_products_warning")}</span> 
          ${this._translateService.instant("cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.product_no_campaign_type_selected.text")}
        </p>`;
    } else if (
      this.isAnyRowInvalid ||
      this.hasExportFilesFromDifferentGs1Groups
    ) {
      const hasMandatoryProductValidation =
        this.form.value.retailer?.hasMandatoryProductValidation;

      confirmOptions.image = "warning-modal-image";

      if (hasMandatoryProductValidation) {
        confirmOptions.showYes = false;
        confirmOptions.template = `
        <p class="dialog-title">${this._translateService.instant("cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.mandatory_validation.title")}</p>
        <p>
          <span class="bold">${this._translateService.instant("cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.mandatory_validation.text")}</span> 
        </p> 
        <ul>
          <li>${this._translateService.instant("cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.mandatory_validation.instruction_1")}</li>
          <li>${this._translateService.instant("cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.mandatory_validation.instruction_2")}</li>
        </ul>`;
      } else {
        if (confirmOptions.showYes && this.isOfferModifiedAfterSent) {
          confirmOptions.yes =
            "cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.confirm_changes";
        }
        confirmOptions.template = `
        <p class="dialog-title">${this._translateService.instant(this.isOfferModifiedAfterSent ? "cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.text_changes" : "cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.text")}</p>
        <p>
          <span class="bold">${this._translateService.instant("cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.invalid_products_warning")}</span> 
          ${this._translateService.instant(this.isOfferModifiedAfterSent ? "cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.invalid_products_text1_changes" : "cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.invalid_products_text1")}
        </p> 
        <p>
          ${this._translateService.instant(this.isOfferModifiedAfterSent ? "cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.invalid_products_text2_changes" : "cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.invalid_products_text2")}
        </p>`;
      }
    } else {
      confirmOptions.image = "ok-modal-image";
      let confirmMessageText =
        "cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.text";
      if (this.hasExportFilesFromDifferentGs1Groups) {
        const retailerId = this.form.value.retailer?.id;
        if (retailerId == RETAILER_ID.KAUBAMAJA) {
          confirmMessageText =
            "cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.alcohol_products_text";
        } else if (retailerId == RETAILER_ID.PRISMA) {
          confirmMessageText =
            "cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.fresh_food_products_text";
        } else if (retailerId == RETAILER_ID.MAXIMA) {
          confirmMessageText =
            "cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.drinks_products_text";
        }
      }
      confirmOptions.template = `
        <p class="dialog-title">${this._translateService.instant("cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.title")}</p>
        <p>
          ${this._translateService.instant(confirmMessageText)}
        </p>
        `;
    }

    const sendOfferSuccessHandler = () => {
      this.offerLastSentDate = moment();

      this._message.ok({
        message: "cards.offer.base_info.send_successful"
      });

      if (
        this.form.value.retailer?.id == RETAILER_ID.RIMI_EST &&
        this.form.value.offerType?.id == OfferType.Campaign
      ) {
        window.open("https://irimi.rimibaltic.com/", "_blank");
      }

      this.setCurrentUserAsOfferOwner();

      setTimeout((_) => {
        this._cd.detectChanges();
      });
    };

    const sendOfferErrorHandler: (error: HttpBadRequestResponse | HttpUnauthorizedResponse) => void = (error) => {
      this._message.error({
        messages: [
          "cards.offer.base_info.send_failed",
          error.validationSummary
        ]
      });

      setTimeout((_) => {
        this._cd.detectChanges();
      });
    };

    this._dialog.confirm(confirmOptions, (ref) => {
      this._cd.detectChanges();
      this._offerService.sendOffer(this.offerId).result(
        null,
        (res) => {
          sendOfferSuccessHandler();
          ref.close();
        },
        (error) => {
          const isEmailMessageSizeLimitExceededError = error.validationSummary == "common.error.email_message_size_limit_exceeded";
          if (isEmailMessageSizeLimitExceededError) {
            this.actionLoading["send"] = false;
            this._cd.detectChanges();

            confirmOptions.image = "warning-modal-image";
            confirmOptions.yes = "cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.email_message_size_limit_exceeded.yes";
            confirmOptions.no = "cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.email_message_size_limit_exceeded.no";
            confirmOptions.template = confirmOptions.template = `
            <p class="dialog-title">${this._translateService.instant("cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.email_message_size_limit_exceeded.title")}</p>
            <p>${this._translateService.instant("cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.email_message_size_limit_exceeded.text")}</p>
            <br/>
            <p>${this._translateService.instant("cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.email_message_size_limit_exceeded.yes_text")}</p>
            <p>${this._translateService.instant("cards.offers.offer_dialogs.offer_supplier_offer_saved_dialog_send.email_message_size_limit_exceeded.no_text")}</p>`;

            this._dialog.confirm(confirmOptions, (limitExceededRef) => {
              this._cd.detectChanges();
              this._offerService.sendOffer(this.offerId, true).result(
                null,
                (r) => {
                  sendOfferSuccessHandler();
                  limitExceededRef.close();
                },
                sendOfferErrorHandler,
                "send",
                this.actionLoading
              );
              this._cd.detectChanges();
            });
          } else {
            sendOfferErrorHandler(error);
          }
        },
        "send",
        this.actionLoading
      );
      this._cd.detectChanges();
    });
  }

  private setCurrentUserAsOfferOwner() {
    let currentOwner = this.form.get("offerOwner").value;
    if (!currentOwner) {
      const currentUser = this.ownerOptions.find(
        (x) => x.id == this.appState.user.id
      );
      this.form.get("offerOwner").setValue(currentUser);
    }
  }

  public downloadImages() {
    this._dialog.open(OfferExportDialogComponent, {
      resolve: () =>
        this._offerService
          .exportProductImages(this.offerId)
          .pipe(
            mergeMap(async (res) => {
              const blob = await this._offerService
                .downloadProductImages(res.id)
                .toPromise();
              return {
                fileName: res.fileName,
                blob: blob
              };
            }),
            catchError((err) => {
              console.error(err);
              return EMPTY;
            })
          )
          .pipe(
            map((res) => {
              const url = window.URL.createObjectURL(res.blob);

              const link = this._download.nativeElement;
              link.href = url;
              link.download = res.fileName;
              link.click();

              window.URL.revokeObjectURL(url);
            })
          )
    });
  }

  private _revertToInitialData() {
    this.form.setValue(this._snapshot);
    const retailer = this.form.get("retailer").value;
    const recipientUserIds = this.form.get("recipients").value?.map(x => x.userId);
    const recipients = retailer?.purchaseManagers?.filter(x => recipientUserIds?.includes(x.userId));
    this._updateAssistantsOptions(recipients);
  }

  private _saveInitialData() {
    this._snapshot = this.form.value;
  }

  public deleteOffer() {
    this._dialog.confirm(
      {
        title: "cards.offers.offer_dialogs.delete_offer.title",
        message: "cards.offers.offer_dialogs.delete_offer.text"
      },
      () => {
        this._offerService.deleteOffer(this.offerId).result(null, () => {
          this._message.ok({
            message: "cards.offers.offer_dialogs.delete_offer.offer_deleted"
          });

          this._router.navigate(["/offers"]);
        });
      }
    );
  }

  public accept() {
    let confirmOptions = {
      image: "confirm-offer-supplier-image",
      yes: "cards.offers.offer_dialogs.supplier_offer_accept.action.confirm",
      no: "cards.offers.offer_dialogs.supplier_offer_accept.action.cancel",
      template: `
      <p class="dialog-title">${this._translateService.instant("cards.offers.offer_dialogs.supplier_offer_accept.text")}</p> 
      <p>
        <span class="bold">${this._translateService.instant("global.attention_uppercase")}</span>
        <span>${this._translateService.instant("cards.offers.offer_dialogs.supplier_offer_accept.attention_text")}</span>
      </p>
      `,
      swapButtons: false
    };

    this._dialog.confirm(confirmOptions, (ref) => {
      this._cd.detectChanges();
      this._offerService.acceptSupplierOffer(this.offerId).result(
        null,
        (res) => {
          this._message.ok({
            message: "cards.offer.base_info.accept_successful"
          });

          ref.close();

          setTimeout((_) => {
            this._cd.detectChanges();
          });
        },
        (error) => {
          this._message.error({
            message: "cards.offer.base_info.accepting_failed"
          });
          setTimeout((_) => {
            this._cd.detectChanges();
          });
        },
        "accept",
        this.actionLoading
      );
    });
  }

  public ngOnDestroy() {
    // this._pipedriveService.removeLeadbooster();

    if (
      this._signalRService.isHubConnected(this._hubConnection) &&
      this.offerId
    ) {
      this._hubConnection.send("leaveOffer", this.offerId);
      this._hubConnection.off("statusChanged");
      this._hubConnection.off("offerHasChanged");
      this._hubConnection.off("offerLocked");
      this._hubConnection.off("offerUnlocked");
    }

    if (this._signalRSubscription) {
      this._signalRSubscription.unsubscribe();
    }

    if (this._offerTitleChangeSubscription) {
      this._offerTitleChangeSubscription.unsubscribe();
    }

    if (this._msgRef) {
      this._msgRef.dismiss();
    }

    if (this._isProductsPdfDisabledSubscription) {
      this._isProductsPdfDisabledSubscription.unsubscribe();
    }

    // if (this._isLockedMessageSubscription) {
    //   this._isLockedMessageSubscription.unsubscribe();
    // }
  }

}

@Component({
  selector: "supplier-offer-import-product-dialog",
  host: { class: "dialog" },
  templateUrl: "../../dialogs/import-product.dialog.html",
  styleUrls: ["../../dialogs/import-product.dialog.scss"]
})
export class SupplierOfferImportProductDialog {
  @ViewChild("download") private _download: ElementRef;

  public files: any = [];
  public fileId: string;

  public isFileLoading = false;
  public isProcessing = false;
  public invalidProducts = false;

  public importInfo: any;

  private offerId: string;
  private offerType: any;

  public form: FormGroup;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<SupplierOfferImportProductDialog>,
    private _fileService: FileService,
    private _importsService: ImportsService,
    private _offerService: OfferService,
    private _exportService: ExportService,
    private _message: LxmMessage
  ) {
    this.offerId = data.offerId;
    this.offerType = data.offerType;

    this.form = new FormGroup({
      isSalePriceRequired: new FormControl(true)
    });
  }

  public get isProcurementOffer() {
    return this.offerType.id == OfferType.Procurement.toString();
  }

  public get importInstructions() {
    if (this.isProcurementOffer) {
      return {
        prelude:
          "cards.offer.import_product_dialog.fruitsVegetables.info.prelude",
        text: [
          "cards.offer.import_product_dialog.fruitsVegetables.info.text1",
          "cards.offer.import_product_dialog.fruitsVegetables.info.text2",
          "cards.offer.import_product_dialog.fruitsVegetables.info.text3"
        ]
      };
    } else {
      return {
        prelude: "cards.offer.import_product_dialog.info.prelude",
        text: [
          "cards.offer.import_product_dialog.info.text1",
          "cards.offer.import_product_dialog.info.text2",
          "cards.offer.import_product_dialog.info.text3"
        ]
      };
    }
  }

  public get isImportInfoValid() {
    if (this.importInfo?.summary == null) {
      return true;
    }

    return this.importInfo?.summary?.isValid;
  }

  public get validationErrorsCount() {
    let total = 0;
    let results = this.importInfo?.summary?.results;

    for (let key in results) {
      if (results.hasOwnProperty(key)) {
        let prop = results[key];
        total += prop.errors.length;
      }
    }

    return total;
  }

  public uploadFile(fileList: FileList) {
    const file = fileList[0];

    const dto = {
      id: "",
      name: file.name,
      progress: 0,
      customText: null
    };
    this.files.push(dto);

    const formData = new FormData();
    formData.append("file", file);

    this._fileService.upload(formData).subscribe(async (event) => {
      switch (event.type) {
        case HttpEventType.UploadProgress:
          dto.progress = Math.round((100 * event.loaded) / event.total);
          this.isFileLoading = true;
          break;

        case HttpEventType.Response:
          await this.getImportData(event.body)
            .then((res) => {
              dto.id = event.body;
              this.fileId = event.body;
            })
            .catch(() => {
              this.deleteAttachment(0);
            });

          this.isFileLoading = false;
          break;
      }
    });
  }

  public deleteAttachment(index: number) {
    event.stopImmediatePropagation();

    if (!this.fileId || !this.files[index]) {
      return;
    }

    this.invalidProducts = false;
    this.importInfo = null;

    this.isFileLoading = true;
    this._fileService
      .delete(this.files[index].id)
      .toPromise()
      .then((res) => {
        this.files?.splice(index, 1);
        this.fileId = null;
        this.isFileLoading = false;
        this.importInfo = null;
      })
      .catch((e) => {
        this.isFileLoading = false;
      });
  }

  public onNoClick(): void {
    this.dialogRef.close(null);
  }

  public closeDialog() {
    this.dialogRef.close(null);
  }

  public downloadOfferImportXls() {
    const exportOfferXls = this.isProcurementOffer
      ? this._offerService.exportFruitsVegetablesOfferImportXls(this.offerId)
      : this._offerService.exportOfferImportXls(this.offerId);

    exportOfferXls
      .pipe(
        mergeMap(async (res) => {
          const blob = await this._offerService.downloadXls(res.id).toPromise();
          return {
            fileName: res.fileName,
            blob: blob
          };
        })
      )
      .pipe(
        map((res) => {
          const url = window.URL.createObjectURL(res.blob);

          const link = this._download.nativeElement;
          link.href = url;
          link.download = res.fileName;
          link.click();

          window.URL.revokeObjectURL(url);
        })
      )
      .toPromise();
  }

  public import() {
    this.isProcessing = true;
    const f = this.form.value;

    var req = {
      offerId: this.offerId,
      fileId: this.fileId,
      uniqueCode: UniqueCodeTypeOption.EanOrSupplierCode,
      includeProductsWithoutSalePrice: !f.isSalePriceRequired
    };

    const importOfferProducts = this.isProcurementOffer
      ? this._importsService.importFruitsVegetablesOfferProducts(req)
      : this._importsService.importOfferProducts(req);

    importOfferProducts.result(
      null,
      (res: any) => {
        this.invalidProducts = false;
        this.isProcessing = false;
        this.deleteAttachment(0);

        this._message.ok({
          message: "cards.import.messages.import_success"
        });

        this.dialogRef.close({ success: true });
      },
      (err) => {
        this.invalidProducts =
          err.validationSummary == "import.error.failed_to_create_import_data";
        //this.validationResults = err.validationResults;

        this.isProcessing = false;

        if (err.validationSummary) {
          this._message.error({
            message: "cards." + err.validationSummary
          });
        }
      }
    );
  }

  public viewErrors() {
    const validationData = this.importInfo.summary.results;

    function getCsvData() {
      let arr = [];
      let columns = ["Row"];
      Object.keys(validationData).forEach((x) => {
        const row = parseInt(x.match(/\d+/)[0], 10);
        const field = x.split(".")[1];
        if (!columns.includes(field)) {
          columns.push(field);
        }
        const value = validationData[x];
        arr[row] = arr[row] || {};
        if (field && value)
          Object.assign(arr[row], {
            Row: row,
            [field]: value.errors.map((q) => q.error)
          });
      });

      return {
        data: arr.filter((row) => row !== null),
        columns
      };
    }

    const { data, columns } = getCsvData();
    this._exportService.exportToCsv(data, "errors", columns);
  }

  private async getImportData(fileId = this.fileId) {
    if (fileId) {
      this.files[0].customText = "cards.import.import_main.import_info_loading";
      this.importInfo = null;

      const getImportData = this.isProcurementOffer
        ? this._importsService.getLxmFruitsVegetablesOfferProductsImportData(
          fileId,
          UniqueCodeTypeOption.EanOrSupplierCode
        )
        : this._importsService.getLxmOfferProductsImportData(
          fileId,
          UniqueCodeTypeOption.EanOrSupplierCode
        );

      getImportData
        .toPromise()
        .then((res: any) => {
          this.importInfo = res.offerProductsImport;
          this.files[0].customText =
            "cards.import.import_main.import_info_loading_complete";
        })
        .catch((e) => {
          this.files[0].progress = -1;
          this.files[0].error =
            "cards.import.import_main.import_info_loading_error";
        });
    }
  }
}
